import { CSSProperties } from 'react';

/**
 * Icon component
 *
 * Simple SVG icon system. Icons are inline SVG for easy styling.
 * Will be replaced with custom icon font/sprites later.
 */

export type IconName =
  | 'select'
  | 'token'
  | 'measure'
  | 'fog'
  | 'draw'
  | 'dice'
  | 'trash'
  | 'sword'
  | 'wand'
  | 'bow'
  | 'sparkle'
  | 'save'
  | 'shield'
  | 'heart'
  | 'skull'
  | 'target'
  | 'move'
  | 'attack'
  | 'defend'
  | 'flee'
  // Intent icons
  | 'crosshairs'
  | 'medic'
  | 'castle'
  | 'barrier'
  | 'horn'
  | 'handshake'
  | 'trap'
  // Condition icons
  | 'eye-off'
  | 'heart-pulse'
  | 'alert'
  | 'lock'
  | 'ghost'
  | 'snowflake'
  | 'droplet'
  | 'chevron-down'
  | 'chevron-up'
  | 'chevron-left'
  | 'chain'
  | 'star'
  | 'moon'
  | 'crystal'
  | 'question'
  // Dice icons
  | 'd4'
  | 'd6'
  | 'd8'
  | 'd10'
  | 'd12'
  | 'd20'
  // UI icons
  | 'chevron-right'
  | 'plus'
  | 'minus'
  | 'arrow-up'
  | 'arrow-down'
  | 'check'
  // Playback icons
  | 'pause'
  | 'play'
  | 'slow'
  | 'fast-forward'
  | 'lightning'
  // Additional badge icons
  | 'flame'
  | 'eye'
  | 'crown'
  | 'scroll'
  | 'close'
  | 'copy'
  // Map/generator icons
  | 'layers'
  | 'edit'
  | 'loader'
  | 'map'
  // Token management icons
  | 'users'
  | 'refresh-cw'
  | 'upload'
  | 'expand'
  // Faction & psyche icons
  | 'flag'
  | 'brain'
  | 'compass'
  // Connection status icons
  | 'wifi'
  | 'wifi-off'
  // Iso view
  | 'diamond';

interface IconProps {
  name: IconName;
  size?: number | string;
  color?: string;
  className?: string;
  style?: CSSProperties;
}

// SVG path data for each icon
const ICON_PATHS: Record<IconName, string> = {
  // Toolbar icons
  select: 'M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3zm10 10l2 6 1-3.5L19.5 14l-6.5-1z',
  token: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z',
  measure: 'M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h2v4h2V8h2v4h2V8h2v4h2V8h2v4h2V8h2v8z',
  fog: 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
  draw: 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z',
  dice: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM7.5 18c-.83 0-1.5-.67-1.5-1.5S6.67 15 7.5 15s1.5.67 1.5 1.5S8.33 18 7.5 18zm0-9C6.67 9 6 8.33 6 7.5S6.67 6 7.5 6 9 6.67 9 7.5 8.33 9 7.5 9zm4.5 4.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5 4.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm0-9c-.83 0-1.5-.67-1.5-1.5S15.67 6 16.5 6s1.5.67 1.5 1.5S17.33 9 16.5 9z',
  trash: 'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z',

  // Character class icons
  sword: 'M14.5 2.5L2 15l2.5 2.5L17 5l-2.5-2.5zM2.5 19l2.5 2.5L7 19.5 4.5 17 2.5 19zm18-14l-2.5-2.5-2 2 2.5 2.5 2-2zM8.5 21.5L11 19l-2.5-2.5-2.5 2.5 2.5 2.5z',
  wand: 'M7.5 5.6L10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zm12 9.8L17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29c-.39-.39-1.02-.39-1.41 0L1.29 18.96c-.39.39-.39 1.02 0 1.41l2.34 2.34c.39.39 1.02.39 1.41 0L16.7 11.05c.39-.39.39-1.02 0-1.41l-2.33-2.35z',
  bow: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3H9c0-1.66 1.34-3 3-3zm5.25 12.5l-1.5-1.5c-.69.44-1.48.75-2.33.91V19h-2.84v-2.09c-.85-.16-1.64-.47-2.33-.91l-1.5 1.5-2-2 1.5-1.5c-.44-.69-.75-1.48-.91-2.33H5v-2.84h2.34c.16-.85.47-1.64.91-2.33l-1.5-1.5 2-2 1.5 1.5c.69-.44 1.48-.75 2.33-.91V5h2.84v2.09c.85.16 1.64.47 2.33.91l1.5-1.5 2 2-1.5 1.5c.44.69.75 1.48.91 2.33H19v2.84h-2.34c-.16.85-.47 1.64-.91 2.33l1.5 1.5-2 2z',
  sparkle: 'M12 3L9.5 9.5 3 12l6.5 2.5L12 21l2.5-6.5L21 12l-6.5-2.5L12 3z',
  save: 'M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z',

  // Combat icons
  shield: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z',
  heart: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z',
  skull: 'M12 2C6.48 2 2 6.48 2 12v8h4v-2h2v2h4v-2h2v2h4v-2h2v-6c0-5.52-4.48-10-10-10zm-2 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm4 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z',
  target: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-14c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',

  // Intent icons
  move: 'M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z',
  attack: 'M12 2l-5.5 9h11z M6.5 17h11l-5.5 9z',
  defend: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z',
  flee: 'M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7',

  // More intent icons
  crosshairs: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17.93c-3.94-.49-7-3.85-7-7.93s3.05-7.44 7-7.93v2.02c-2.84.48-5 2.94-5 5.91s2.16 5.43 5 5.91v2.02zm6-7.93c0 2.97-2.16 5.43-5 5.91v-2.02c1.74-.44 3-2.01 3-3.89s-1.26-3.44-3-3.88V6.09c2.84.48 5 2.94 5 5.91z',
  medic: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z',
  castle: 'M21 9V7l-2-2V2h-2v2h-2V2h-2v2h-2V2H9v2H7V2H5v3L3 7v2l2 1v10h14V10l2-1zm-4 8h-2v-3h-2v3h-2v-3H9v3H7V7.17l2-2 2 2v2h2v-2l2-2 2 2V17z',
  barrier: 'M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm6 9.09c0 4-2.55 7.7-6 8.83V12H6V6.31l6-2.12v5.81h6v3.09z',
  horn: 'M12 12c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6-5.5c0-.83-.67-1.5-1.5-1.5H16V3h-2v2h-4V3H8v2h-.5c-.83 0-1.5.67-1.5 1.5v3.5h12V6.5zM19 10H5c-1.1 0-2 .9-2 2v9h2v-3h14v3h2v-9c0-1.1-.9-2-2-2zm0 6H5v-4h14v4z',
  handshake: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
  trap: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',

  // Condition icons
  'eye-off': 'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z',
  'heart-pulse': 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35zM16.5 5c-1.22 0-2.4.62-3.07 1.64L12 8.45l-1.43-1.81C9.9 5.62 8.72 5 7.5 5 5.53 5 4 6.53 4 8.5c0 2.94 2.87 5.54 7.22 9.29L12 18.46l.78-.67C17.13 14.04 20 11.44 20 8.5 20 6.53 18.47 5 16.5 5z',
  alert: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
  lock: 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z',
  ghost: 'M12 2c-5.52 0-10 4.48-10 10v10l3-3 3 3 3-3 3 3 3-3 3 3V12c0-5.52-4.48-10-10-10zm-1 14H9v-2h2v2zm0-4H9V8h2v4zm4 4h-2v-2h2v2zm0-4h-2V8h2v4z',
  snowflake: 'M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22v-2z',
  droplet: 'M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z',
  'chevron-down': 'M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z',
  'chevron-up': 'M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z',
  'chevron-left': 'M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z',
  chain: 'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z',
  star: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z',
  moon: 'M9 2c-1.05 0-2.05.16-3 .46 4.06 1.27 7 5.06 7 9.54 0 4.48-2.94 8.27-7 9.54.95.3 1.95.46 3 .46 5.52 0 10-4.48 10-10S14.52 2 9 2z',
  crystal: 'M12 2L2 12l10 10 10-10L12 2zm0 2.83L19.17 12 12 19.17 4.83 12 12 4.83z',
  question: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z',

  // Dice specific icons
  'd4': 'M12 2L2 19h20L12 2zm0 4l6.5 11h-13L12 6z',
  'd6': 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z',
  'd8': 'M12 2L2 12l10 10 10-10L12 2zm0 2.83L19.17 12 12 19.17 4.83 12 12 4.83z',
  'd10': 'M12 2L3 12l9 10 9-10L12 2zm0 3l6 7-6 7-6-7 6-7z',
  'd12': 'M12 1L3 7.5v9L12 23l9-6.5v-9L12 1zm0 2.5l6.5 4.7v6.6L12 19.5l-6.5-4.7V8.2L12 3.5z',
  'd20': 'M12 2L2 8v8l10 6 10-6V8L12 2zm0 2.5L18.5 8v6L12 17.5 5.5 14V8L12 4.5zm0 3a4.5 4.5 0 100 9 4.5 4.5 0 000-9z',

  // UI icons
  'chevron-right': 'M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z',
  'plus': 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
  'minus': 'M19 13H5v-2h14v2z',
  'arrow-up': 'M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z',
  'arrow-down': 'M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z',
  'check': 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',

  // Playback icons
  'pause': 'M6 19h4V5H6v14zm8-14v14h4V5h-4z',
  'play': 'M8 5v14l11-7z',
  'slow': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-2-3.5l6-4.5-6-4.5v9z',
  'fast-forward': 'M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z',
  'lightning': 'M7 2v11h3v9l7-12h-4l4-8z',

  // Additional badge icons
  'flame': 'M13.5 0.67s0.74 2.65 0.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l0.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5 0.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z',
  'eye': 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
  'crown': 'M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z',
  'scroll': 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10H7v-2h10v2zm0-4H7V7h10v2z',
  'close': 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
  'copy': 'M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z',
  // Map/generator icons
  'layers': 'M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z',
  'edit': 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z',
  'loader': 'M12 4V2C6.48 2 2 6.48 2 12h2c0-4.42 3.58-8 8-8zm0 16c-4.42 0-8-3.58-8-8H2c0 5.52 4.48 10 10 10v-2zm8-8c0-4.42-3.58-8-8-8V2c5.52 0 10 4.48 10 10h-2zm0 0c0 4.42-3.58 8-8 8v2c5.52 0 10-4.48 10-10h-2z',
  'map': 'M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z',
  // Token management icons
  'users': 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  'refresh-cw': 'M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z',
  'upload': 'M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z',
  'expand': 'M21 11V3h-8l3.29 3.29-10 10L3 13v8h8l-3.29-3.29 10-10L21 11z',
  // Faction & psyche icons
  'flag': 'M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z',
  'brain': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-2.52 1.18-4.76 3-6.22V9c0 1.1.9 2 2 2h2v2c0 .55.45 1 1 1h2v3h-3.14c-.47 1.21-1.3 2.2-2.36 2.86-.18.1-.36.2-.55.28.6.18 1.3.36 2.05.36 4.42 0 8-3.58 8-8s-3.58-8-8-8zM11 10H9V8h2v2zm4 0h-2V8h2v2z',
  'compass': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-5.31-4.69L10 12l3.31 1.69L14 17l-3.31-1.69L7.38 14l3.31-1.69L14 9l-3.31 1.69L7.38 12l3.31 1.69L14 15.31z',
  // Connection status icons
  'wifi': 'M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z',
  'wifi-off': 'M22.99 9C19.15 5.16 13.8 3.76 8.84 4.78l2.52 2.52c3.47-.17 6.99 1.05 9.63 3.7l2-2zM8.73 6.73L4.26 2.26 3 3.53l2.04 2.04C2.61 7.24 1 9.04 1 9l2 2c1.61-1.61 3.53-2.72 5.6-3.35l2.52 2.52c-1.91.29-3.75 1.13-5.27 2.52l2.02 2.02c1.69-1.31 3.73-1.97 5.78-1.97.32 0 .64.02.95.07l2.43 2.43c-.73.36-1.41.83-2.02 1.45l2.02 2.02c.96-.88 2.14-1.5 3.42-1.78l2.02 2.02L22.47 21l1.26-1.27L8.73 6.73zM9 17l3 3 3-3c-1.65-1.66-4.34-1.66-6 0z',

  // Iso view
  diamond: 'M12 2L2 12l10 10 10-10L12 2zm0 2.83L19.17 12 12 19.17 4.83 12 12 4.83z',
};

export function Icon({ name, size = 24, color = 'currentColor', className, style }: IconProps) {
  const path = ICON_PATHS[name];

  if (!path) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      className={className}
      style={style}
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

export default Icon;
